import React, { useState, useEffect } from "react";

import clsx from "clsx";
import moment from "jalali-moment";

import styles from "./styles.module.scss";
import StepCounter from "./StepCounter";
import { Modal, ModalBody } from "components";
import useModal from "hooks/useModal.js";
import { CloseOutlineIconComponent, PopupUpdateComponent } from "assets/icons";
import { parseStringifiedJsonObj } from "utils/object.js";

// as this popup has pagination. there are two identifiers for it.
//  popupName is the key stored in the localstorage and the other one is popupId which is the identifier for the latest content added
const PopupMessageWithPagination = ({
  popupContent,
  popupName,
  popupId,
  type = "update",
}) => {
  const [now, setNow] = useState(moment());
  const [step, setStep] = useState(0);
  const [steps, setSteps] = useState();
  const [notShowPopupAgain, setNotShowPopupAgain] = useState(false);

  const isUpdate = type === "update";
  const headerTitle = isUpdate ? "آپدیت جدید" : "اطلاعیه";

  const [visible, , hideModal, showModal] = useModal();

  const getShowPopupStatus = () => {
    const popupStringObject = localStorage.getItem(popupName);

    const popupObj = parseStringifiedJsonObj(popupStringObject);

    const status = popupObj?.status;
    const id = popupObj?.id;

    const shouldShowPopup =
      id !== popupId || !["never", "nextLogin"].includes(status);
    return shouldShowPopup;
  };

  const handleChangePopupDisplay = () => {
    setNotShowPopupAgain((prev) => !prev);
  };

  const handleClosePopup = () => {
    const status = notShowPopupAgain ? "never" : "nextLogin";

    const obj = { status, id: popupId };
    const stringifiedObj = JSON.stringify(obj);
    localStorage.setItem(popupName, stringifiedObj);

    hideModal();
  };

  const dayOfWeek = now.locale("fa").format("dddd");
  const time = now.format("HH:mm:ss");
  const options = { year: "numeric", month: "long", day: "numeric" };
  const date = new Date().toLocaleDateString("fa-IR", options);

  const hasPagination = popupContent?.length > 1;
  const reachedEnd = step + 1 === popupContent?.length;

  const handlePageIncrement = () => setStep(step + 1);
  const handlePageDecrement = () => setStep(step - 1);

  const renderDecreaseButton = (
    <button className={styles.pagination_btn} onClick={handlePageDecrement}>
      قبلی
    </button>
  );

  const renderIncreaseButton = (
    <button className={styles.pagination_btn} onClick={handlePageIncrement}>
      بعدی
    </button>
  );

  const renderButtons = () => {
    if (reachedEnd) {
      return (
        <>
          {hasPagination && step > 0 && reachedEnd && renderDecreaseButton}

          {reachedEnd && (
            <button
              onClick={handleClosePopup}
              className={clsx([styles.popup_close_btn, styles.pagination_btn])}
            >
              تایید
            </button>
          )}
        </>
      );
    }

    return (
      <>
        {hasPagination &&
          step > 0 &&
          step + 1 !== popupContent?.length &&
          renderDecreaseButton}

        {hasPagination &&
          step + 1 < popupContent?.length &&
          renderIncreaseButton}
      </>
    );
  };

  const renderPopupHeader = (
    <div className={styles.popup_headers_wrapper}>
      {/* header */}
      <div className={styles.popup_header}>
        <span onClick={handleClosePopup} className={styles.close_popup}>
          <CloseOutlineIconComponent />
        </span>

        <div>
          {popupContent?.[step]?.headerLink && (
            <a
              rel="noreferrer"
              href={popupContent?.[step]?.headerLink}
              target="_blank"
            >
              {popupContent?.[step]?.headerLink}
            </a>
          )}
          <h4>
            {dayOfWeek} - {date}
          </h4>

          <h5>{time}</h5>
        </div>
      </div>
      <div className={styles.body_header_wrapper}>
        <div className={styles.popup_body_header}>
          <StepCounter
            percentage={(100 / steps) * (step + 1)}
            step={step + 1}
            steps={steps}
          />

          <div className={styles.popup_update_title}>
            <div>
              <p>{headerTitle}</p>
              {isUpdate && <PopupUpdateComponent />}
            </div>

            <span>
              {dayOfWeek} - {date}
            </span>
          </div>
        </div>
        {isUpdate && <h2>{popupContent?.[step]?.title}</h2>}
      </div>
    </div>
  );

  const renderPopupBody = (
    <>
      <div className={styles.popup_body}>
        <div className={styles.popup_body_content}>
          <p className={styles.content_wrapper}>
            {popupContent?.[step]?.description}
          </p>
        </div>
      </div>
    </>
  );

  const renderPopupFooter = (
    <div className={styles.footer}>
      <div className={styles.popup_not_show_again}>
        <span>
          <input
            type="checkbox"
            checked={notShowPopupAgain}
            onChange={handleChangePopupDisplay}
            id="text"
          />
          <label aria-labelledby="label" htmlFor="text">
            دیگه این پیام رو نمایش نده
          </label>
        </span>
        <div>{renderButtons()}</div>
      </div>

      {popupContent?.[step]?.featureLink && (
        <div className={styles.popup_body_btn}>
          <a
            rel="noreferrer"
            href={popupContent?.[step].featureLink}
            target="_blank"
          >
            مشاهده کامل اپدیت
          </a>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const time = setInterval(() => setNow(moment()), 1000);
    return () => clearInterval(time);
  }, []);

  useEffect(() => {
    if (getShowPopupStatus()) {
      showModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSteps(popupContent?.length);
  }, [popupContent]);

  return (
    <Modal className={styles.popup} visible={visible}>
      <ModalBody classNames={{ containerBody: styles.container_body }}>
        {renderPopupHeader}
        {renderPopupBody}
        {renderPopupFooter}
      </ModalBody>
    </Modal>
  );
};

export default PopupMessageWithPagination;
